import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Console, Hook, Unhook, Decode } from 'console-feed';
import PropTypes from 'prop-types';

import './styles.scss';
import { bindActionCreators } from 'redux';
import { selectContent } from '../../store/EventPanel/selectors';
import { selectContent as selectCodeContent } from '../../store/CodePanel/selectors';
import { selectDarkToggle } from '../../store/NavigationBar/selectors';
import { selectReload } from '../../store/PreviewPanel/selectors';
import ButtonTooltip from '../Tooltip/buttonTooltip';
import { sendGAEvent } from '../../store/GoogleAnalytics/actions';
import {
  CLICK_COPY_EVENTS_BUTTON,
  CLICK_COPY_LOGS_BUTTON,
  CLICK_CLEAR_EVENTS_BUTTON,
  CLICK_CLEAR_LOGS_BUTTON,
  CLICK_CONSOLE_TAB,
  CLICK_EVENTS_TAB,
} from '../../constants/google-analytics';

const EventPanel = ({ propsContent, isDarkTheme, codeContent, reload, onSendGAEvent }) => {
  const eventTab = 'eventTab';
  const consoleTab = 'consoleTab';
  const [eventContent, setContent] = useState('');
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState('eventTab');
  const [showRedCircle, setShowRedCircle] = useState(false);

  // Listen to the console
  useEffect(() => {
    Hook(
      window.console,
      (log) => {
        const decodedLog = Decode(log);
        setLogs((prevLogs) => [...prevLogs, decodedLog]);
        if (decodedLog.method === 'error' && !showRedCircle) {
          setShowRedCircle(true);
        }
      },
      true,
    );
    return () => Unhook(window.console);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `setShowRedCircle` and `operationsDom` should not be passed into the dependencies array
  }, []);

  // Update the event handler tab
  useEffect(() => {
    setContent((prevEventContent) => prevEventContent + propsContent);
  }, [propsContent]);

  // Every time new code is run, the console and event handler should be cleared
  useEffect(() => {
    setContent('');
    setLogs([]);
  }, [codeContent]);

  // Every time the same code is run again, the console and event handler should be cleared
  useEffect(() => {
    if (reload) {
      setContent('');
      setLogs([]);
    }
  }, [reload]);
  const panel = () => {
    if (tab === eventTab) {
      return (
        <pre>
          <code>{eventContent}</code>
        </pre>
      );
    }
    return (
      <Console
        variant="dark"
        logs={logs}
        styles={{
          BASE_FONT_FAMILY: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New',monospace",
          LOG_COLOR: 'inherit',
          LOG_BACKGROUND: 'transparent',
          BASE_COLOR: 'inherit',
          BASE_BACKGROUND_COLOR: 'transparent',
          LOG_ERROR_BACKGROUND: 'transparent',
          LOG_ERROR_BORDER: 'none',
          LOG_ERROR_COLOR: 'rgb(233,63,59)',
        }}
        filter={['log', 'error']}
      />
    );
  };
  return (
    <div
      className={
        isDarkTheme ? 'events-container card-shadow dark' : 'events-container card-shadow '
      }
    >
      <div className="event-container__header card-header">
        <div className="tabs">
          <div
            className={tab === eventTab ? 'tab-selected' : 'tab'}
            onClick={() => {
              onSendGAEvent(CLICK_EVENTS_TAB);
              setTab(eventTab);
            }}
          >
            Events
          </div>
          <div
            className={tab === consoleTab ? 'tab-selected' : 'tab'}
            onClick={() => {
              onSendGAEvent(CLICK_CONSOLE_TAB);
              setTab(consoleTab);
              setShowRedCircle(false);
            }}
          >
            Console
            <span className={showRedCircle ? 'red-circle' : 'red-circle hide'} />
          </div>
        </div>
        <button
          id="copy-events-tooltip"
          className="copy-events"
          onClick={() => {
            if (tab === eventTab) {
              onSendGAEvent(CLICK_COPY_EVENTS_BUTTON);
              navigator.clipboard.writeText(eventContent);
            } else {
              onSendGAEvent(CLICK_COPY_LOGS_BUTTON);
              // Convert console logs to string
              let result = '';
              if (logs) {
                logs.forEach((log) => {
                  result += `${log.data || ''}\n`;
                });
              }
              navigator.clipboard.writeText(result);
            }
          }}
          type="button"
        >
          <span className="material-icons">copy_all</span>
        </button>
        <button
          id="clear-events-tooltip"
          className="clear-events"
          onClick={() => {
            if (tab === eventTab) {
              onSendGAEvent(CLICK_CLEAR_EVENTS_BUTTON);
              setContent('');
            } else {
              onSendGAEvent(CLICK_CLEAR_LOGS_BUTTON);
              setLogs([]);
              setShowRedCircle(false);
            }
          }}
          type="button"
        >
          <span className="material-icons">delete</span>
        </button>
        <ButtonTooltip
          anchorId="copy-events-tooltip"
          content={<div className="tooltip-text">Copy logs</div>}
        />
        <ButtonTooltip
          anchorId="clear-events-tooltip"
          content={<div className="tooltip-text">Clear logs</div>}
        />
      </div>
      <div
        className="event-container__text"
        onClick={() => {
          if (tab === consoleTab) {
            setShowRedCircle(false);
          }
        }}
      >
        {panel()}
      </div>
    </div>
  );
};

EventPanel.propTypes = {
  propsContent: PropTypes.string.isRequired,
  isDarkTheme: PropTypes.bool.isRequired,
  codeContent: PropTypes.string.isRequired,
  reload: PropTypes.bool.isRequired,
  onSendGAEvent: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    propsContent: selectContent(state),
    isDarkTheme: selectDarkToggle(state),
    codeContent: selectCodeContent(state),
    reload: selectReload(state),
    onSendGAEvent: sendGAEvent,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      onSendGAEvent: sendGAEvent,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(EventPanel);
